import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api";
import { StatCard, SectionTitle, StatusPill, Avatar, EmptyState } from "../../components/Atoms";
import { TrendChart, CategoryPie } from "../../components/Charts";
import { Users, Briefcase, Stack, ClipboardText, CurrencyDollarSimple, ChartLineUp, ArrowRight } from "@phosphor-icons/react";

const StatsSkeleton = () => (
  <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
    {[0,1,2,3,4,5].map(i => <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />)}
  </div>
);

export default function AdminDashboard() {
  // Independent state buckets so each section can render as soon as its data arrives
  const [stats, setStats]         = useState(null);
  const [clients, setClients]     = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recent, setRecent]       = useState(null);

  // 1. Cards first — smallest, fastest payload
  useEffect(() => { api.get("/stats/admin").then(r => setStats(r.data)).catch(() => setStats({})); }, []);
  // 2. Brands grid (medium)
  useEffect(() => { api.get("/clients").then(r => setClients(r.data)).catch(() => setClients([])); }, []);
  // 3. Charts
  useEffect(() => { api.get("/analytics/admin").then(r => setAnalytics(r.data)).catch(() => setAnalytics({ series: [], categories: [] })); }, []);
  // 4. Recent submissions — only need 6, not all 500+
  useEffect(() => { api.get("/submissions?limit=6").then(r => setRecent(r.data)).catch(() => setRecent([])); }, []);

  return (
    <div className="space-y-6">
      <SectionTitle title={`Hey, Admin 👋`} subtitle="Mission control for the whole creator economy." />

      {/* Summary cards — render as soon as /stats/admin resolves */}
      {stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard label="Total Clients" value={stats.total_clients} icon={Briefcase} accent="cyan" />
          <StatCard label="Ambassadors" value={stats.total_ambassadors} icon={Users} accent="pink" />
          <StatCard label="Active Projects" value={stats.active_projects} icon={Stack} accent="purple" />
          <StatCard label="Pending Reviews" value={(stats.pending_applications||0) + (stats.pending_submissions||0)} icon={ClipboardText} accent="amber" />
          <StatCard label="Monthly Payouts" value={"₹" + Number(stats.total_payouts||0).toLocaleString("en-IN")} icon={CurrencyDollarSimple} accent="green" />
          <StatCard label="Completion Rate" value={(stats.completion_rate||0) + "%"} icon={ChartLineUp} accent="lime" />
        </div>
      ) : <StatsSkeleton />}

      {/* Brand / Client cards */}
      <div className="glass p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-white font-bold">Brands</div>
            <div className="text-xs text-muted">Click a brand to see its projects</div>
          </div>
          <Link to="/admin/clients" className="btn-ghost text-xs flex items-center gap-1.5" data-testid="all-clients-link">All clients <ArrowRight size={12} weight="bold" /></Link>
        </div>
        {clients === null ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[0,1,2,3,4,5,6,7].map(i => <div key={i} className="h-16 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : clients.length === 0 ? <EmptyState icon={Briefcase} title="No brands yet" /> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {clients.slice(0, 8).map(c => (
              <Link key={c.id} to={`/admin/clients/${c.id}`} className="p-4 rounded-2xl bg-white/3 border border-white/5 hover:bg-white/5 hover:border-[#22D3EE]/30 transition flex items-center gap-3" data-testid={`dash-client-${c.id}`}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center overflow-hidden flex-shrink-0">
                  {c.logo ? <img src={c.logo} alt={c.company} className="w-full h-full object-cover" /> :
                    <Briefcase size={18} weight="fill" color="white" />}
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm truncate">{c.company}</div>
                  <div className="text-[11px] text-muted truncate">{c.industry || "—"}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="glass p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-white font-bold">Payouts & Submissions</div>
              <div className="text-xs text-muted">Last 6 months</div>
            </div>
          </div>
          {analytics
            ? <TrendChart data={analytics.series || []} />
            : <div className="h-[260px] rounded-xl bg-white/5 animate-pulse" />}
        </div>
        <div className="glass p-5">
          <div className="text-white font-bold mb-1">Project Categories</div>
          <div className="text-xs text-muted mb-3">Distribution</div>
          {analytics
            ? <CategoryPie data={analytics.categories || []} />
            : <div className="h-[260px] rounded-xl bg-white/5 animate-pulse" />}
        </div>
      </div>

      {/* Recent Submissions */}
      <div className="glass p-5">
        <div className="text-white font-bold mb-3">Recent Submissions</div>
        {recent === null ? (
          <div className="space-y-3">
            {[0,1,2].map(i => <div key={i} className="h-14 rounded-2xl bg-white/5 animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {recent.length === 0 && <div className="text-muted text-sm">No submissions yet.</div>}
            {recent.map(s => (
              <Link key={s.id} to={`/admin/projects/${s.project_id}`} className="flex items-center gap-3 p-3 rounded-2xl bg-white/3 border border-white/5 hover:border-[#22D3EE]/30" data-testid={`dash-recent-sub-${s.id}`}>
                <Avatar src={s.ambassador_photo} name={s.ambassador_name} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">{s.task_title}</div>
                  <div className="text-xs text-muted truncate">{s.ambassador_name} · {s.project_title}</div>
                </div>
                <StatusPill status={s.status} />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
